import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { selectAuthToken } from '../store/authSlice';

/**
 * App base URL from env (no trailing slash), e.g. https://demo.dev.webimcloud.com/webimcld
 *
 * If REACT_APP_BASE_URL is missing at build time, API_BASE_URL would become `/api` and fetch
 * would hit http://localhost:3000/api — so in development we fall back to the demo host unless
 * REACT_APP_USE_API_PROXY=true (same-origin /api + src/setupProxy.js for JSESSIONID).
 */
const trimmedBase = String(process.env.REACT_APP_BASE_URL || '').trim().replace(/\/$/, '');

const USE_SAME_ORIGIN_API =
  process.env.REACT_APP_USE_API_PROXY === 'true' || process.env.REACT_APP_USE_API_PROXY === '1';

const DEMO_DEV_BASE = 'https://demo.dev.webimcloud.com/webimcld';

function resolveBaseUrl() {
  if (trimmedBase) return trimmedBase;
  if (USE_SAME_ORIGIN_API) return '';
  if (process.env.NODE_ENV === 'test') return '';
  if (process.env.NODE_ENV === 'production') return '';
  return DEMO_DEV_BASE;
}

export const BASE_URL = resolveBaseUrl();

/** API root for fetchBaseQuery baseUrl */
export const API_BASE_URL = BASE_URL ? `${BASE_URL}/api` : '/api';

const FORM_URLENCODED = 'application/x-www-form-urlencoded; charset=UTF-8';

const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  // credentials: 'include',
  prepareHeaders: (headers, { getState, endpoint }) => {
    headers.set('Accept', 'application/json, text/plain, */*');
    try {
      const jwt = selectAuthToken(getState())?.trim();
      if (jwt) {
        headers.set('jwttoken', jwt)
      }
    } catch {
      /* ignore */
    }
    if (endpoint !== 'uploadInventoryLoad') {
      headers.set('Content-Type', FORM_URLENCODED);
    }
    return headers;
  },
});

export const uploadApi = createApi({
  reducerPath: 'uploadApi',
  baseQuery,
  endpoints: (builder) => ({
    uploadInventoryLoad: builder.mutation({
      query: ({
        file,
        companyName,
        companyNumber,
        companyId,
        companyDivisionNumber,
        companyDivisionId,
        appUserId,
        personId,
        firstName,
        lastName,
        emailAddress
      }) => {
        const body = new FormData();
        body.append('files', file);
        body.append('companyName', companyName ?? '');
        body.append('companyNumber', companyNumber != null ? String(companyNumber) : '');
        body.append('companyId', companyId != null ? String(companyId) : '');
        body.append('companyDivisionNumber', companyDivisionNumber != null ? String(companyDivisionNumber) : '');
        body.append('companyDivisionId', companyDivisionId != null ? String(companyDivisionId) : '');
        body.append('appUserId', appUserId != null ? String(appUserId) : '');
        body.append('personId', personId != null ? String(personId) : '');
        body.append('firstName', firstName != null ? String(firstName) : '');
        body.append('lastName', lastName != null ? String(lastName) : '');
        body.append('emailAddress', emailAddress != null ? String(emailAddress) : '');

        return {
          url: '/inventory/load/upload/jobs',
          method: 'POST',
          body,
          credentials: 'include',
        };
      },
    }),
    getDivision: builder.query({
      query: () => 'getCompanyDivision',
      providesTags: ['CostCenter'],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getCompanyDetailsListByUser: builder.query({
      query: (userId) => ({
        url: 'company-details/listByUser',
        params: { p: userId },
      }),
    }),
    getUserDetailsListByUser: builder.query({
      query: (userId) => ({
        url: `user-details/${userId}`
      }),
    }),
    getUploadStream: builder.query({
      query: (jobId) => ({
        url: 'inventory/load/upload/jobs/7cb768cd-8c94-40a9-96b8-fda305f7d4a2/stream',
      }),
    }),
    getInventoryLoadDefault: builder.query({
      query: () => ({
        url: 'inventory/load/default',
        responseHandler: async (response) => {
          const blob = await response.blob();
          const contentDisposition = response.headers.get('Content-Disposition');
          return { blob, contentDisposition };
        },
      }),
    }),
  }),
});

export const {
  useUploadInventoryLoadMutation,
  useGetDivisionQuery,
  useGetCompanyDetailsListByUserQuery,
  useGetUserDetailsListByUserQuery,
  useGetUploadStreamQuery,
  useLazyGetInventoryLoadDefaultQuery,
} = uploadApi;
